import { isNil } from 'lodash';
import { IconName } from '@shared/components/Icon';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { FilterOption } from '../types';

/**
 * The value-editor bucket a filter falls into - what decides which editor `Selections` renders and
 * which `FilterOneOf` branch `getFilterComponent` builds. Usually the component's own type, but a
 * scalar representation can contribute a bucket of its own that no component type has (see
 * `FilterScalarKeyDescriptor['valueType']`).
 */
export type FilterValueType = API.ComponentType | 'BOOLEAN';

/**
 * How a component type relates to `scalarKey`, which names the indexed scalar representation of a
 * component the backend filters against.
 *
 * The two modes are deliberately exclusive, because they are answers to different questions:
 *
 * - `always` - the component has exactly one representation and it is the only sensible thing to
 *   filter on, so it is applied silently and never surfaced. There is nothing for the user to
 *   choose, and the filter's `FilterOneOf` value is unaffected.
 * - `selectable` - each representation is offered as its own entry in the component-selection
 *   menu, sitting alongside the component in the same list rather than nested under it. Picking
 *   one can change which value editor applies, which is why the choice belongs to that menu rather
 *   than the value editor downstream of it.
 *
 * A component type absent from the map has no scalar representation at all.
 */
type FilterScalarKeys =
  | { mode: 'always'; scalarKey: API.ScalarKey }
  | {
      mode: 'selectable';
      scalarKeys: readonly API.ScalarKey[];
      /**
       * Whether the component's own value is offered as an entry too. `'unsupported'` means it has
       * no value editor, so the representations are the only way to filter on it - a TIME_TRACKER
       * can be filtered on whether it is running, but there is nothing to compare a list of timer
       * entries against.
       */
      bareValue: 'filterable' | 'unsupported';
    };

/**
 * The scalar representations available per component type.
 *
 * Adding an `always` entry needs nothing else. Adding a `selectable` entry needs a matching
 * descriptor below, and - if the representation reads as a value the filter UI can't already edit
 * - a `valueType` with an editor in `Selections` and a `FilterOneOf` branch in
 * `getFilterComponent`.
 */
const FILTER_SCALAR_KEYS: Partial<Record<API.ComponentType, FilterScalarKeys>> = {
  EMAIL: {
    mode: 'selectable',
    scalarKeys: ['EMAIL_VALUE_STRING'],
    bareValue: 'unsupported',
  },
  FILE: {
    mode: 'selectable',
    scalarKeys: ['FILE_NAMES_STRING'],
    bareValue: 'unsupported',
  },
  RELATED_CARD: { mode: 'always', scalarKey: 'RELATED_CARD_IDS' },
  SIGNATURE: {
    mode: 'selectable',
    scalarKeys: ['SIGNATURE_SIGNED_DATE', 'SIGNATURE_SIGNED_BY_USER_ID'],
    bareValue: 'unsupported',
  },
  TIME_TRACKER: {
    mode: 'selectable',
    scalarKeys: ['TIME_TRACKER_IS_ACTIVE_BOOLEAN'],
    bareValue: 'unsupported',
  },
  URL: {
    mode: 'selectable',
    scalarKeys: ['URL_VALUE_STRING'],
    bareValue: 'unsupported',
  },
};

/** How one selectable representation presents itself and what it reads as. */
interface FilterScalarKeyDescriptor {
  /**
   * Names the representation, and is composed with the component's own label into the entry's
   * label - a TIME_TRACKER called "Timer" reads as "Timer → Is Running". The arrow is what marks
   * it as a reading of that component rather than a component of that name, matching how a
   * cross-workflow filter names the hop it travels, and it reads the same way in the collapsed row
   * button as in the menu - which a secondary text line couldn't.
   *
   * The entry is not nested under the component either: at one line it sits in the list at the
   * position the component would occupy, and stacking a second expandable level under a related
   * card would make for a long, deeply indented menu.
   */
  label: string;
  icon: IconName;
  /** Omitted when the representation reads as the component's own type. */
  valueType?: FilterValueType;
}

/**
 * Only `selectable` representations need a descriptor - an `always` one is never surfaced, so it
 * has no label or icon of its own and always reads as the component's own type.
 */
const FILTER_SCALAR_KEY_DESCRIPTORS: Partial<Record<API.ScalarKey, FilterScalarKeyDescriptor>> = {
  EMAIL_VALUE_STRING: {
    label: i18n('card.actions.filters.scalarKey.emailValue'),
    icon: 'envelope',
    valueType: 'TEXT',
  },
  FILE_NAMES_STRING: {
    label: i18n('card.actions.filters.scalarKey.fileNames'),
    icon: 'paperclip',
    valueType: 'TEXT',
  },
  SIGNATURE_SIGNED_BY_USER_ID: {
    label: i18n('card.actions.filters.scalarKey.signatureSignedBy'),
    icon: 'person-heavy',
    valueType: 'PERSON',
  },
  SIGNATURE_SIGNED_DATE: {
    label: i18n('card.actions.filters.scalarKey.signatureSignedDate'),
    icon: 'calendar',
    valueType: 'DATE',
  },
  TIME_TRACKER_IS_ACTIVE_BOOLEAN: {
    label: i18n('card.actions.filters.scalarKey.timeTrackerIsActive'),
    icon: 'start-play-timer',
    valueType: 'BOOLEAN',
  },
  URL_VALUE_STRING: {
    label: i18n('card.actions.filters.scalarKey.urlValue'),
    icon: 'link-add',
    valueType: 'TEXT',
  },
};

function getFilterScalarKeys(componentType: string | undefined): FilterScalarKeys | undefined {
  return isNil(componentType) ? undefined : FILTER_SCALAR_KEYS[componentType as API.ComponentType];
}

/**
 * Resolves the `scalarKey` a filter should carry, or `null` when it should carry none.
 *
 * For an `always` component type this is fixed by the type. Otherwise it is whatever the user
 * selected alongside the component (`FilterOption['scalarKey']`), which is usually nothing - most
 * filters read the component's own value and send no `scalarKey`.
 */
export function getFilterScalarKey(
  filter: Pick<FilterOption, 'type' | 'scalarKey'> | undefined,
): API.ScalarKey | null {
  const scalarKeys = getFilterScalarKeys(filter?.type);
  if (scalarKeys?.mode === 'always') {
    return scalarKeys.scalarKey;
  }
  return filter?.scalarKey ?? null;
}

/**
 * The value-editor bucket for a filter, once its scalar representation is taken into account.
 * Falls back to the component's own type, which is the answer for every filter that reads the
 * component's own value.
 */
export function getFilterValueType(
  filter: Pick<FilterOption, 'type' | 'scalarKey'> | undefined,
): string | undefined {
  const scalarKey = getFilterScalarKey(filter);
  const valueType = isNil(scalarKey)
    ? undefined
    : FILTER_SCALAR_KEY_DESCRIPTORS[scalarKey]?.valueType;
  return valueType ?? filter?.type;
}

export interface FilterScalarKeyOption {
  scalarKey: API.ScalarKey;
  /** The representation's own label, to be composed with the component's. */
  label: string;
  icon: IconName;
}

/**
 * The scalar representations of `componentType` to offer as entries of their own in a
 * component-selection menu, in the order they should appear after the component itself.
 * Empty for every component type whose scalar key (if any) is applied unconditionally.
 */
export function getFilterScalarKeyOptions(
  componentType: string | undefined,
): FilterScalarKeyOption[] {
  const scalarKeys = getFilterScalarKeys(componentType);
  if (scalarKeys?.mode !== 'selectable') {
    return [];
  }
  return scalarKeys.scalarKeys.flatMap(scalarKey => {
    const descriptor = FILTER_SCALAR_KEY_DESCRIPTORS[scalarKey];
    return isNil(descriptor) ? [] : [{ scalarKey, label: descriptor.label, icon: descriptor.icon }];
  });
}

/**
 * Whether the component's own value should be offered as a filter entry. False only for component
 * types that exist in the menu purely through their scalar representations.
 */
export function canFilterComponentValue(componentType: string | undefined): boolean {
  const scalarKeys = getFilterScalarKeys(componentType);
  return scalarKeys?.mode === 'selectable' ? scalarKeys.bareValue === 'filterable' : true;
}
